import { useNavigate } from '@tanstack/react-router';
import type { App, Project } from '@hangar/shared';
import { useProfiles } from '@/api/hooks';
import { useCan, type Can } from '@/lib/permissions';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { cn } from '@/lib/utils';
import { useSeesProviders } from '@/lib/useAudience';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { RowActions, type RowAction } from '@/components/RowActions';
import { FRAMEWORK_ICON, FRAMEWORK_LABEL, Tag } from '@/components/badges';
import { ProviderBadge } from '@/components/ProviderBadge';
import { bundleIdOf } from '@/features/getting-started/useSetup';
import { bundleIdLabel } from './appPageModel';

interface Props {
  app: App;
  project: Project;
  onEdit: () => void;
  /** Opens the archive confirmation (the more menu's "Archive app"). */
  onArchive: () => void;
}

type Nav = ReturnType<typeof useNavigate>;

/** The more menu: the builds / env / apps shortcuts, plus Archive (hidden without access, disabled with a reason). */
function moreActions(app: App, project: Project, can: Can, navigate: Nav, onArchive: () => void): RowAction[] {
  return [
    { key: 'builds', label: 'Builds of this app', icon: 'construction', testId: 'app-more-builds', onSelect: () => void navigate({ to: '/projects/$slug/builds', params: { slug: project.slug }, search: { app: app.slug } }) },
    { key: 'env', label: 'Environment variables', icon: 'tune', testId: 'app-more-env', onSelect: () => void navigate({ to: '/projects/$slug/env', params: { slug: project.slug }, search: { scope: 'app-env', app: app.slug } }) },
    { key: 'apps', label: 'All apps of the project', icon: 'apps', testId: 'app-more-apps', onSelect: () => void navigate({ to: '/projects/$slug', params: { slug: project.slug }, search: { app: app.slug } }) },
    ...(can.hidden ? [] : [{ key: 'archive', label: 'Archive app', icon: 'archive', testId: 'app-more-archive', destructive: true, disabledReason: can.allowed ? undefined : can.reason, onSelect: onArchive }]),
  ];
}

function AppTile({ framework }: { framework: App['framework'] }) {
  return (
    <span className="relative flex size-14 shrink-0 items-center justify-center rounded-xl border bg-card shadow-card">
      <Icon name={FRAMEWORK_ICON[framework]} size={28} className={cn('text-muted-foreground', framework === 'android_native' && 'text-android')} />
    </span>
  );
}

/** Which build service the app is linked to: platform operators only (SPEC decision 23). */
function AppProvider({ app }: { app: App }) {
  const operator = useSeesProviders();
  const provider = app.easProjectId ? 'eas' : app.bitriseAppSlug ? 'bitrise' : null;
  if (!operator) return null;
  return provider ? <ProviderBadge kind={provider} /> : <Tag title="No EAS project or Bitrise app linked yet">no provider</Tag>;
}

function BundleId({ label }: { label: NonNullable<ReturnType<typeof bundleIdLabel>> }) {
  return (
    <>
      <span aria-hidden className="text-subtle">
        ·
      </span>
      <span className={cn('min-w-0 truncate', label.known ? 'text-foreground' : 'font-sans text-subtle')} title={label.known ? 'Bundle id its profiles check' : 'The build reads the bundle id from app.json or app.config'}>
        {label.text}
      </span>
    </>
  );
}

function AppIdentity({ app, bundleId }: { app: App; bundleId: string | undefined }) {
  const label = bundleIdLabel(bundleId, app.framework);
  return (
    <div className="min-w-0 flex-1">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1.5">
        <h1 className="min-w-0 truncate text-2xl font-semibold tracking-tight text-foreground sm:text-3xl" data-testid="app-name" title={app.name}>
          {app.name}
        </h1>
        <AppProvider app={app} />
      </div>
      <p className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[13px] text-muted-foreground">
        <span>{FRAMEWORK_LABEL[app.framework]}</span>
        {label ? <BundleId label={label} /> : null}
        <span aria-hidden className="text-subtle">
          ·
        </span>
        <span className="min-w-0 truncate text-subtle">{app.slug}</span>
      </p>
    </div>
  );
}

function HeaderActions({ app, can, phone, more, onEdit }: { app: App; can: Can; phone: boolean; more: RowAction[]; onEdit: () => void }) {
  return (
    <div className="flex shrink-0 items-center gap-2">
      <GatedButton data-testid="app-edit-settings" variant={phone ? 'outline' : 'default'} can={can} onClick={onEdit} className="max-sm:flex-1">
        <Icon name="tune" size={18} />
        Edit settings
      </GatedButton>
      <RowActions actions={more} subject={app.name} variant="menu" testId="app-more" className="size-9 border bg-secondary text-muted-foreground max-sm:size-11" />
    </div>
  );
}

/**
 * Design 29 header: the app tile, the name with its provider badge, `framework · bundle id · slug` in mono, then
 * "Edit settings" (the page's one primary action) and a more menu (builds, env vars, Apps & profiles, Archive app, which
 * replaced the Danger zone tab so the tabs fit one row on a phone).
 */
export function AppHeader({ app, project, onEdit, onArchive }: Props) {
  const navigate = useNavigate();
  const can = useCan('projects:write', project.id);
  // Phones already carry the orange Run build button: Edit settings steps back to outline there (design 29 mobile).
  const phone = useBreakpoint() === 'phone';
  const profiles = useProfiles(app.id);
  const bundleId = bundleIdOf(profiles.data);
  const more = moreActions(app, project, can, navigate, onArchive);
  return (
    <header className="enter flex flex-col gap-4 border-b pb-5 sm:flex-row sm:items-start">
      <div className="flex min-w-0 flex-1 items-start gap-4">
        <AppTile framework={app.framework} />
        <AppIdentity app={app} bundleId={bundleId} />
      </div>
      <HeaderActions app={app} can={can} phone={phone} more={more} onEdit={onEdit} />
    </header>
  );
}
